import React, { useState, useEffect, useCallback, createContext, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import PortalHeader from "./PortalHeader";
import PortalSidebar from "./PortalSidebar";
import SessionWarningModal from "../SessionWarningModal";
import useSessionTimeout from "../../hooks/useSessionTimeout";
import { isTokenValid } from "../../utils/auth";
import "../../css/PortalLayout.css";

const PortalLayoutContext = createContext(null);
export const usePortalLayout = () => useContext(PortalLayoutContext);

export default function PortalLayout({
  children,
  tokenKey,
  dataKey,
  loginPath,
  portalTitle = "Portal",
  badgeColor = "teal",
  BadgeIcon,
  sidebarSections = null,
  routePrefix = "",
  showSwitchToApp = false,
  userNameField = "nombre",
  companyField = "company",
  activeColor,
  activeBg,
}) {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dynamicSections, setDynamicSections] = useState(sidebarSections);

  useEffect(() => {
    setDynamicSections(sidebarSections);
  }, [sidebarSections]);

  // Auth guard
  useEffect(() => {
    const token = sessionStorage.getItem(tokenKey);
    const data = sessionStorage.getItem(dataKey);

    if (!token || !data) {
      navigate(loginPath);
      return;
    }

    if (!isTokenValid(tokenKey)) {
      sessionStorage.removeItem(tokenKey);
      sessionStorage.removeItem(dataKey);
      navigate(loginPath);
      return;
    }

    try {
      setUserData(JSON.parse(data));
    } catch {
      sessionStorage.removeItem(tokenKey);
      sessionStorage.removeItem(dataKey);
      navigate(loginPath);
    }
  }, [navigate, tokenKey, dataKey, loginPath]);

  const handleLogout = useCallback(() => {
    sessionStorage.removeItem(tokenKey);
    sessionStorage.removeItem(dataKey);
    navigate(loginPath);
  }, [navigate, tokenKey, dataKey, loginPath]);

  const {
    showWarning,
    remainingFormatted,
    extendSession,
    logout: sessionLogout,
    warningReason,
  } = useSessionTimeout({
    timeoutMinutes: 20,
    warningMinutes: 2,
    onTimeout: handleLogout,
    onTokenRefresh: useCallback(async () => {}, []),
    tokenKey,
    enabled: !!sessionStorage.getItem(tokenKey),
  });

  const contextValue = useMemo(() => ({
    setSidebarSections: setDynamicSections,
    sidebarOpen,
    setSidebarOpen,
    setUserData,
  }), [sidebarOpen]);

  if (!userData) return null;

  const companyName = userData[companyField] || "";

  return (
    <PortalLayoutContext.Provider value={contextValue}>
      <div className="portal-wrapper">
        <PortalHeader
          userData={userData}
          companyName={companyName}
          portalTitle={portalTitle}
          badgeColor={badgeColor}
          BadgeIcon={BadgeIcon}
          onLogout={handleLogout}
          onToggleSidebar={dynamicSections ? () => setSidebarOpen((prev) => !prev) : null}
          showSwitchToApp={showSwitchToApp}
          tokenKey={tokenKey}
        />
        <div className="portal-body">
          {dynamicSections && (
            <PortalSidebar
              sections={dynamicSections}
              open={sidebarOpen}
              onClose={() => setSidebarOpen(false)}
              routePrefix={routePrefix}
              activeColor={activeColor}
              activeBg={activeBg}
            />
          )}
          <main className="portal-content">
            {children}
          </main>
        </div>
        <SessionWarningModal
          show={showWarning}
          remainingTime={remainingFormatted}
          onExtend={extendSession}
          onLogout={sessionLogout}
          reason={warningReason}
        />
      </div>
    </PortalLayoutContext.Provider>
  );
}
